import { useCallback, useEffect, useRef, useState } from 'react'
import { Maximize2, Minimize2, Pause, Play, Volume2, VolumeX } from 'lucide-react'
import { accent } from '@/lib/tokens'

interface DocsVideoProps {
  src: string
  poster: string
  ariaLabel?: string
  autoStart?: boolean
}

function formatTime(seconds: number) {
  const s = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  const m = Math.floor(s / 60)
  const r = Math.floor(s % 60)
  return `${m}:${r.toString().padStart(2, '0')}`
}

export function DocsVideo({ src, poster, ariaLabel = 'Play extension walkthrough', autoStart = false }: DocsVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const scrubberRef = useRef<HTMLDivElement | null>(null)
  const hideTimer = useRef<number | null>(null)

  const [started, setStarted] = useState(autoStart)
  const [posterHover, setPosterHover] = useState(false)

  const [playing, setPlaying] = useState(false)
  const [firstFrameReady, setFirstFrameReady] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [bufferedEnd, setBufferedEnd] = useState(0)
  const [muted, setMuted] = useState(autoStart)
  const [volume, setVolume] = useState(1)
  const [fullscreen, setFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [scrubbing, setScrubbing] = useState(false)
  const [hoverPct, setHoverPct] = useState<number | null>(null)
  const [volumeHover, setVolumeHover] = useState(false)

  const togglePlay = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (v.paused || v.ended) void v.play()
    else v.pause()
  }, [])

  const toggleMute = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    v.muted = !v.muted
    if (!v.muted && v.volume === 0) v.volume = 1
  }, [])

  const setVolumeValue = useCallback((value: number) => {
    const v = videoRef.current
    if (!v) return
    const clamped = Math.max(0, Math.min(1, value))
    v.volume = clamped
    v.muted = clamped === 0
  }, [])

  const toggleFullscreen = useCallback(async () => {
    const el = wrapperRef.current
    if (!el) return
    if (!document.fullscreenElement) await el.requestFullscreen()
    else await document.exitFullscreen()
  }, [])

  const seekFromClientX = useCallback((clientX: number) => {
    const el = scrubberRef.current
    const v = videoRef.current
    if (!el || !v || !Number.isFinite(v.duration) || v.duration <= 0) return
    const rect = el.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    v.currentTime = pct * v.duration
    setCurrentTime(pct * v.duration)
  }, [])

  useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === wrapperRef.current)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  // Autoplay starts muted by default; user unmutes via the volume control
  useEffect(() => {
    if (!autoStart) return
    const v = videoRef.current
    if (!v) return
    v.muted = true
    v.play().catch(() => {})
  }, [autoStart])

  useEffect(() => {
    if (!scrubbing) return
    const onMove = (e: MouseEvent) => seekFromClientX(e.clientX)
    const onUp = () => setScrubbing(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [scrubbing, seekFromClientX])

  useEffect(() => {
    if (!started) return
    const onKey = (e: KeyboardEvent) => {
      const el = wrapperRef.current
      if (!el) return
      const active = document.activeElement
      const inScope = el.contains(active) || active === document.body
      if (!inScope) return
      const tag = (active?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea') return
      if (e.key === ' ' || e.key.toLowerCase() === 'k') { e.preventDefault(); togglePlay() }
      else if (e.key.toLowerCase() === 'm') { toggleMute() }
      else if (e.key.toLowerCase() === 'f') { void toggleFullscreen() }
      else if (e.key === 'ArrowLeft') {
        const v = videoRef.current; if (v) v.currentTime = Math.max(0, v.currentTime - 5)
      } else if (e.key === 'ArrowRight') {
        const v = videoRef.current; if (v) v.currentTime = Math.min(v.duration || 0, v.currentTime + 5)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [started, togglePlay, toggleMute, toggleFullscreen])

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current)
    hideTimer.current = window.setTimeout(() => {
      const v = videoRef.current
      if (v && !v.paused && !scrubbing) setShowControls(false)
    }, 2500)
  }, [scrubbing])

  const nudgeControls = useCallback(() => {
    setShowControls(true)
    scheduleHide()
  }, [scheduleHide])

  useEffect(() => {
    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current)
    }
  }, [])

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0
  const bufferedPct = duration > 0 ? (bufferedEnd / duration) * 100 : 0

  // Poster state — click-to-play entry
  if (!started) {
    const vignette = posterHover ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)'
    const btnShadow = posterHover
      ? '0 16px 40px rgba(242, 107, 29, 0.5), 0 0 0 12px rgba(242, 107, 29, 0.12)'
      : '0 12px 32px rgba(242, 107, 29, 0.4), 0 0 0 8px rgba(242, 107, 29, 0.1)'
    const btnScale = posterHover ? 1.06 : 1

    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          paddingBottom: '56.25%',
          borderRadius: 12,
          overflow: 'hidden',
          background: '#fafaf9',
        }}
      >
        <button
          type="button"
          aria-label={ariaLabel}
          onClick={() => setStarted(true)}
          onMouseEnter={() => setPosterHover(true)}
          onMouseLeave={() => setPosterHover(false)}
          onFocus={() => setPosterHover(true)}
          onBlur={() => setPosterHover(false)}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            padding: 0, border: 'none',
            background: `url("${poster}") center / cover no-repeat`,
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, background: vignette, transition: 'background 200ms ease-out' }}
          />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '68%',
              left: '9%',
              width: 88, height: 88,
              borderRadius: '50%',
              background: accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: btnShadow,
              transform: `translate(-50%, -50%) scale(${btnScale})`,
              transition: 'transform 200ms ease-out, box-shadow 200ms ease-out',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ marginLeft: 3 }}>
              <path d="M4 3l9 5-9 5V3z" fill="#ffffff" />
            </svg>
          </span>
        </button>
      </div>
    )
  }

  // Playback state — custom player
  const controlsVisible = showControls || !playing || scrubbing

  return (
    <div
      ref={wrapperRef}
      onMouseMove={nudgeControls}
      onMouseLeave={() => { if (playing && !scrubbing) setShowControls(false) }}
      style={{
        position: 'relative',
        width: '100%',
        paddingBottom: fullscreen ? 0 : '56.25%',
        height: fullscreen ? '100%' : undefined,
        borderRadius: fullscreen ? 0 : 12,
        overflow: 'hidden',
        background: '#000',
      }}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        playsInline
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload noplaybackrate"
        onContextMenu={(e) => e.preventDefault()}
        onClick={togglePlay}
        onPlay={() => { setPlaying(true); scheduleHide() }}
        onPlaying={() => { setFirstFrameReady(true); setPlaying(true); scheduleHide() }}
        onPause={() => { setPlaying(false); setShowControls(true) }}
        onEnded={() => { setPlaying(false); setShowControls(true) }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onVolumeChange={(e) => {
          setMuted(e.currentTarget.muted)
          setVolume(e.currentTarget.volume)
        }}
        onProgress={(e) => {
          const b = e.currentTarget.buffered
          if (b.length) setBufferedEnd(b.end(b.length - 1))
        }}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          objectFit: 'contain',
          background: '#000',
          cursor: playing && !controlsVisible ? 'none' : 'pointer',
        }}
      />

      {/* Poster hold-over — matches title-card framing until first real frame plays */}
      {!firstFrameReady && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: `url("${poster}") center / cover no-repeat`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* Center play overlay when paused (after first frame has rendered) */}
      {!playing && firstFrameReady && (
        <button
          type="button"
          aria-label={playing ? 'Pause' : 'Play'}
          onClick={togglePlay}
          style={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 72, height: 72,
            borderRadius: '50%',
            background: accent,
            border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 12px 32px rgba(242, 107, 29, 0.45), 0 0 0 10px rgba(242, 107, 29, 0.12)',
            zIndex: 2,
          }}
        >
          <svg width="26" height="26" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ marginLeft: 2 }}>
            <path d="M4 3l9 5-9 5V3z" fill="#ffffff" />
          </svg>
        </button>
      )}

      {/* Bottom control bar */}
      <div
        aria-hidden={!controlsVisible}
        style={{
          position: 'absolute',
          left: 0, right: 0, bottom: 0,
          padding: '40px 14px 10px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 100%)',
          opacity: controlsVisible ? 1 : 0,
          transform: controlsVisible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 180ms ease-out, transform 180ms ease-out',
          pointerEvents: controlsVisible ? 'auto' : 'none',
          zIndex: 3,
        }}
      >
        {/* Scrubber */}
        <div
          ref={scrubberRef}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
          tabIndex={0}
          onMouseDown={(e) => { setScrubbing(true); seekFromClientX(e.clientX) }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            setHoverPct(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)))
          }}
          onMouseLeave={() => setHoverPct(null)}
          style={{
            position: 'relative',
            height: 14,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            marginBottom: 6,
          }}
        >
          {/* track */}
          <div style={{ position: 'relative', width: '100%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${bufferedPct}%`, background: 'rgba(255,255,255,0.4)' }} />
            {hoverPct !== null && (
              <div style={{ position: 'absolute', inset: 0, width: `${hoverPct * 100}%`, background: 'rgba(255,255,255,0.25)' }} />
            )}
            <div style={{ position: 'absolute', inset: 0, width: `${progressPct}%`, background: accent }} />
          </div>
          {/* thumb */}
          <div
            style={{
              position: 'absolute',
              left: `${progressPct}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: scrubbing || hoverPct !== null ? 14 : 10,
              height: scrubbing || hoverPct !== null ? 14 : 10,
              borderRadius: '50%',
              background: accent,
              boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
              transition: 'width 120ms ease-out, height 120ms ease-out',
            }}
          />
        </div>

        {/* Button row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <IconButton onClick={togglePlay} label={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause size={18} fill="#fff" /> : <Play size={18} fill="#fff" />}
          </IconButton>

          <div
            onMouseEnter={() => setVolumeHover(true)}
            onMouseLeave={() => setVolumeHover(false)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconButton onClick={toggleMute} label={muted || volume === 0 ? 'Unmute' : 'Mute'}>
              {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </IconButton>
            <div
              style={{
                width: volumeHover ? 72 : 0,
                opacity: volumeHover ? 1 : 0,
                overflow: 'hidden',
                transition: 'width 180ms ease-out, opacity 180ms ease-out',
                display: 'flex', alignItems: 'center',
              }}
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.02}
                value={muted ? 0 : volume}
                onChange={(e) => setVolumeValue(parseFloat(e.target.value))}
                aria-label="Volume"
                style={{
                  width: '100%',
                  accentColor: accent,
                  cursor: 'pointer',
                  background: 'transparent',
                }}
              />
            </div>
          </div>

          <span
            style={{
              fontFamily: "'Geist Mono Variable', monospace",
              fontSize: 12,
              color: 'rgba(255,255,255,0.85)',
              marginLeft: 4,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatTime(currentTime)} <span style={{ color: 'rgba(255,255,255,0.55)' }}>/ {formatTime(duration)}</span>
          </span>

          <div style={{ flex: 1 }} />

          <IconButton onClick={toggleFullscreen} label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {fullscreen ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
          </IconButton>
        </div>
      </div>
    </div>
  )
}

function IconButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34, height: 34,
        borderRadius: 6,
        border: 'none',
        background: hover ? 'rgba(255,255,255,0.12)' : 'transparent',
        color: '#fff',
        cursor: 'pointer',
        transition: 'background 150ms ease-out',
        padding: 0,
      }}
    >
      {children}
    </button>
  )
}
